<template>
  <app-modal :model-value="isDialogOpen"
             size="xs"
             :ok-text="okText"
             :ok-loading="isPending"
             data-locator="documents-rename-dialog"
             @cancel="prepareCloseRenameDocument"
             @ok="prepareRenameDocument"
  >
    <template #title>
      <template v-if="homeLayout">
        <div class="font-weight-bold">
          {{ $t('project.documents.dialogs.DocumentsRenameDialog.renameProjectsFolder', { name: projectsFolderToRename.name }) }}
        </div>
      </template>
      <template v-else-if="documentToRename.type === 'folder'">
        {{ $t('project.documents.dialogs.DocumentsRenameDialog.renameFolder') }}
      </template>
      <template v-else>
        {{ $t('project.documents.dialogs.DocumentsRenameDialog.renameFile') }}
      </template>
    </template>
    <template #body>
      <form @submit.prevent="prepareRenameDocument"
      >
        <AppTextField v-model.trim="v$.newName.$model"
                      :label="$t(`project.documents.dialogs.DocumentsRenameDialog.${homeLayout ? 'folderName' : 'lblRenameDocument'}`)"
                      :counter="nameMaxLength"
                      :error-messages="newNameErrors"
                      :maxlength="nameMaxLength"
                      autofocus
        />
      </form>
    </template>
  </app-modal>
</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { mapState, mapMutations, mapActions } from 'vuex'

import AppTextField from '@/common/AppTextField.vue'
import { DIRECTORY_NAME_MAX_LENGTH } from '@/common/utils/directory'
import { FILE_NAME_MAX_LENGTH } from '@/common/utils/files'
import { formFieldErrors } from '@/common/utils/form'
import { toast } from '@/common/utils/toast'
import { LOAD_FOLDERS_TREE } from '@/store/modules/documents/action_types'
import { CLOSE_DOCUMENT_RENAMING_DIALOG, RENAME_DOCUMENT } from '@/store/modules/documents-rename/action_types'
import { LOAD_PROJECTS_FOLDERS_TREE, GET_PROJECTS_FOLDER_BY_ID } from '@/store/modules/projects-folders/action_types'
import { UPDATE_PROJECTS_FOLDERS_TREE } from '@/store/modules/projects-folders/mutation_types'
import { RENAME_PROJECTS_FOLDER } from '@/store/modules/projects-folders-rename/action_types'
import {
  SET_PROJECTS_FOLDER_RENAME_DIALOG_IS_OPEN,
  SET_PROJECTS_FOLDER_TO_RENAME,
} from '@/store/modules/projects-folders-rename/mutation_types'

export default {
  name: 'DocumentsRenameDialog',
  components: { AppTextField },
  setup: () => ({ v$: useVuelidate() }),
  validations: {
    newName: {
      required,
      maxLength: (value, vm) => value.length <= vm.nameMaxLength,
    },
  },
  data () {
    return {
      newName: '',
    }
  },
  computed: {
    ...mapState('global', ['homeLayout']),
    ...mapState('documentsRename', ['loading', 'isOpen', 'documentToRename']),
    ...mapState('projectsFolders', ['currentProjectsFolder', 'projectsFoldersTree']),
    ...mapState('projectsFoldersRename', ['projectsFolderRenamePending', 'projectsFolderRenameDialogIsOpen', 'projectsFolderToRename']),
    isPending () {
      return this.homeLayout ? this.projectsFolderRenamePending : this.loading
    },
    isDialogOpen () {
      return this.homeLayout ? this.projectsFolderRenameDialogIsOpen : this.isOpen
    },
    okText () {
      return this.homeLayout ? this.$t('common.register') : this.$t('project.documents.dialogs.DocumentsRenameDialog.rename')
    },
    newNameErrors() {
      return formFieldErrors(
          this.v$.newName,
          [
            ['required', this.$t('common.validations.fieldRequired', { fieldName: this.$t('project.documents.dialogs.DocumentsRenameDialog.lblRenameDocument') })],
            ['maxLength', this.$t('common.validations.maxLength', { fieldName: this.$t('project.documents.dialogs.DocumentsRenameDialog.lblRenameDocument'), maxLength: this.nameMaxLength })],
          ],
      )
    },
    nameMaxLength () {
      if (this.homeLayout) {
        return DIRECTORY_NAME_MAX_LENGTH
      }
      return this.documentToRename.type === 'folder' ? DIRECTORY_NAME_MAX_LENGTH : FILE_NAME_MAX_LENGTH
    },
  },
  mounted () {
    if (this.homeLayout) {
      this.newName = this.projectsFolderToRename.name
    } else if (this.documentToRename.type === 'folder') {
      this.newName = this.documentToRename.name
    } else if (this.documentToRename.type === 'file') {
      this.newName = this.documentToRename.basename
    }
  },
  methods: {
    ...mapActions('documents', [LOAD_FOLDERS_TREE]),
    ...mapActions('documentsRename', [RENAME_DOCUMENT, CLOSE_DOCUMENT_RENAMING_DIALOG]),
    ...mapActions('projectsFolders', [LOAD_PROJECTS_FOLDERS_TREE, GET_PROJECTS_FOLDER_BY_ID]),
    ...mapMutations('projectsFolders', [UPDATE_PROJECTS_FOLDERS_TREE]),
    ...mapMutations('projectsFoldersRename', [SET_PROJECTS_FOLDER_RENAME_DIALOG_IS_OPEN, SET_PROJECTS_FOLDER_TO_RENAME]),
    ...mapActions('projectsFoldersRename', [RENAME_PROJECTS_FOLDER]),
    async prepareRenameDocument () {
      if (this.isPending) {
        return
      }

      this.v$.$touch()
      if (!this.v$.$invalid) {
        if (this.homeLayout) {
          try {
            const projectsFolderToRenameId = this.projectsFolderToRename.id
            await this.RENAME_PROJECTS_FOLDER(this.newName)

            const updateFolderName = (folders) => {
              const folder = folders.find(f => f.id === projectsFolderToRenameId)
              if (folder) folder.name = this.newName
            }

            if (this.currentProjectsFolder?.id) {
              updateFolderName(this.currentProjectsFolder.children || [])
              this.UPDATE_PROJECTS_FOLDERS_TREE(this.currentProjectsFolder)
            } else {
              updateFolderName(this.projectsFoldersTree)
            }

            this.SET_PROJECTS_FOLDER_RENAME_DIALOG_IS_OPEN(false)
          } catch (error) {
            toast.error(this.$t('project.documents.dialogs.DocumentsRenameDialog.renameFolderError'))
          }
        } else {
          const needFoldersTreeRefreshAfterRenaming = this.documentToRename.type === 'folder'

          await this.RENAME_DOCUMENT(this.newName)
          this.newName = ''

          if (needFoldersTreeRefreshAfterRenaming) {
            await this.LOAD_FOLDERS_TREE()
          }
        }
      }
    },
    prepareCloseRenameDocument () {
      if (this.homeLayout) {
        this.SET_PROJECTS_FOLDER_RENAME_DIALOG_IS_OPEN(false)
        this.SET_PROJECTS_FOLDER_TO_RENAME({})
      } else {
        this.CLOSE_DOCUMENT_RENAMING_DIALOG()
      }
      this.newName = ''
    },
  },
}
</script>
